import React, { useState, useRef, useEffect } from 'react';
import Images from 'assets/images';
import { useApiMutation } from 'hooks/useApiMutation';
import { createTicketAction } from 'api/actions/SupportActions';
import useToastify from 'hooks/useToastify';
import ContentEditable from 'react-contenteditable';
import emojiRegex from 'emoji-regex';
import Button from 'components/buttons/Button';
import Spacer from 'components/Spacer/Spacer';
import { useApiQuery } from 'hooks/useApiQuery';
import { getPatientsAction } from 'api/actions/UserActions';
import { useCurrentUser } from 'hooks/useCurrentUser';

function CreateTicketModal({ onClose, onSuccess }) {
  const MAX_FILES = 4;
  const template = Array.from(Array(MAX_FILES)).map((_) => ({
    uri: "",
    file: "",
    data: "",
  }));

  const [patientOptions, setPatientOptions] = useState([]);
  const [subject, setSubject] = useState("");
  const [message, setMessage] = useState("");
  const [patientId, setPatientId] = useState("");
  const [fileArray, setFileArray] = useState(template);
  const inputRef = useRef();
  const { showAlert } = useToastify();
  const emojiRegexValue = emojiRegex();

  const { mutate: callCreateTicket, loading: creatingTicket } = useApiMutation(createTicketAction);

  const { userId } = useCurrentUser();
  const {
    loading: fetchingPatients,
    payload: patientsList
  } = useApiQuery(getPatientsAction(userId));

  useEffect(() => {
    if (patientsList) {
      setPatientOptions(patientsList?.map((patient) => ({value: patient?.id, label: patient?.full_name})));
    }
  }, [patientsList]);

  const getTextContent = (html) => {
    if (!html) return "";
    
    const tempDiv = document.createElement("div");
    tempDiv.innerHTML = html;

    return tempDiv.textContent || tempDiv.innerText || "";
  };

  const hasValidMessage = () => {
    const textContent = getTextContent(message);
    return textContent.trim().length > 0;
  };

  const messageTextContent = getTextContent(message);
  const isEmojiIncluded = 
    !!(messageTextContent?.length && messageTextContent.match(emojiRegexValue));

  const isPDF = (string) =>
    string?.includes(".pdf") || string?.includes(".PDF");
  
  const isFileDataAvailable = fileArray?.some((el) => el?.data?.length);
  const isButtonEnabled = 
    (subject?.trim()?.length && hasValidMessage() && !creatingTicket) && patientId;

  function handleFileChange(event, index) {
    const { target } = event;
    const { files } = target;

    if (files && files[0]) {
        let reader = new FileReader();
        reader.onload = (event) => {
          const obj = {
            uri: files?.[0]?.name,
            file: files[0],
            data: event?.target?.result,
          };

          setFileArray(
            fileArray.map((el, idx) => {
              if (idx === index) {
                return obj;
              } else {
                return el;
              }
            })
          );
        };

        reader.readAsDataURL(files[0]);
    }
  }

  async function handleSubmit() {
    if (isEmojiIncluded) {
      showAlert("Emojis are not allowed.", "error");
      return;
    }

    if (!isButtonEnabled) {
      return;
    }


    try {
      const formData = new FormData();
      formData.append("support_ticket", JSON.stringify({ subject, message, patient_id: patientId }));

      if (isFileDataAvailable) {
        const allFiles = fileArray?.filter((el) => el?.data?.length);
        allFiles.forEach((file) => {
          formData.append("attachments[]", file.file);
        });
      }

      const result = await callCreateTicket({
        payload: formData,
      });

      if (result?.payload && !result?.error) {
        showAlert("Support ticket created successfully!", "success");
        onSuccess && onSuccess(result?.payload);
        onClose();
      } else {
        showAlert(
          result?.payload?.error || 
          result?.payload?.message || 
          "Failed to create support ticket. Please try again.",
          "error"
        );
      }
    } catch (error) {
      showAlert("Failed to create support ticket. Please try again.", "error");
    }
  }

  return (
    <div className="h-screen bg-black bg-opacity-20 px-4">
      <div className="flex items-center justify-center h-full overflow-y-auto wrapper">
        <div className="flex flex-col items-center justify-center w-full px-6 bg-white rounded-lg pb-8 md:w-10/12 max-w-4xl">
          <div className="flex items-center justify-between w-full px-2 pt-6 pb-4 border-b">
            <h2 className="text-3xl font-bold">Create Support Ticket</h2>
            <img
              onClick={creatingTicket ? null : onClose}
              src={Images.closeIcon}
              alt="close"
              className="w-10 h-10 cursor-pointer"
            />
          </div>

          <div className="w-full px-2 py-6">
            {/* Subject Field */}
            <div className="mb-6">
              <label className="block text-indigo/50 text-2xl font-bold mb-2">Subject <span className="text-red">*</span></label>
              <input
                type="text"
                value={subject}
                onChange={(e) => setSubject(e.target.value)}
                placeholder="Enter a brief subject for your query"
                className="w-full px-4 py-3 text-lg border font-medium border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                maxLength={100}
              />
              <p className="font-medium text-indigo/50 mt-1">{subject.length}/100 characters</p>
            </div>

            {/* Message Field */}
            <div className="mb-6">
              <label className="block text-indigo/50 text-2xl font-bold mb-2">Message <span className="text-red">*</span></label>
              <ContentEditable
                className="w-full px-4 py-3 text-lg border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent min-h-[120px] break-words font-medium"
                innerRef={inputRef}
                html={message}
                onChange={(e) => setMessage(e?.target?.value)}
                tagName="div"
                placeholder="Describe your issue or question in detail..."
              />
              {isEmojiIncluded ? (
                <p className="mt-2 text-lg font-medium text-red">
                  Emojis are not allowed.
                </p>
              ) : null}
            </div>
            <Spacer height={10} />
            <label htmlFor="patient-select" className="block text-indigo/50 text-2xl font-bold mb-2">Patient <span className="text-red">*</span></label>
            <div className="relative rounded-lg h-14 bg-white border-solid border w-full border-borderColor pl-6 pr-12">
              <select disabled={fetchingPatients} id="patient-select" className={`h-full bg-transparent font-medium text-lg ${
                !patientOptions?.length ? "opacity-50" : "opacity-100"
                } text-indigo w-full outline-none cursor-pointer placeholder-cloudyBlue rounded-none`} 
                value={patientId} onChange={(e) => setPatientId(e.target.value)}
              >
                  <option value="">Select Patient</option>
                  {patientOptions?.map((patientOption) => (
                    <option key={patientOption?.value} value={patientOption?.value}>{patientOption?.label}</option>
                  ))}
              </select>
              <img
                src={Images.dropdown}
                alt="chev_down"
                className={`absolute right-2 transition duration-500 ease-in-out w-6 h-6 transform rotate-0 top-1/2 -translate-y-1/2`}
              />
              </div>
            <Spacer height={10} />
            {/* File Upload Section */}
            <div className="mb-6">
              <label className="block text-indigo/50 text-2xl font-bold mb-2">Attachments (Optional)</label>
              
              {isFileDataAvailable ? (
                <div className="flex flex-wrap items-center justify-start w-full mb-4">
                  {Array(MAX_FILES)
                    .fill("file")
                    ?.map((_, idx) => {
                      return (
                        <div
                          key={idx}
                          className="relative flex items-center justify-center m-2 bg-white border border-dashed cursor-pointer border-indigo w-28 h-28 md:w-32 md:h-32"
                        >
                          <img
                            src={Images.addIcon}
                            alt="add"
                            className="w-6 h-6 md:w-8 md:h-8"
                          />
                          {fileArray?.[idx]?.uri?.length ? (
                            <img
                              src={
                                isPDF(fileArray?.[idx]?.uri)
                                  ? Images.pdfIcon
                                  : fileArray?.[idx]?.data?.length
                                  ? fileArray?.[idx]?.data
                                  : fileArray?.[idx]?.uri
                              }
                              alt="doc"
                              className={`absolute object-contain ${
                                isPDF(fileArray?.[idx]?.uri)
                                  ? "w-1/2 h-1/2"
                                  : "w-full h-full"
                              }`}
                            />
                          ) : null}
                          {isPDF(fileArray?.[idx]?.uri) ? (
                            <p className="absolute text-xs font-medium bottom-1 text-center px-1">
                              {fileArray?.[idx]?.uri?.length > 10
                                ? fileArray?.[idx]?.uri
                                    ?.replace(".pdf", "")
                                    ?.substring(0, 6) + "...pdf"
                                : fileArray?.[idx]?.uri}
                            </p>
                          ) : null}
                          <input
                            type="file"
                            accept="image/*, application/pdf"
                            className="absolute top-0 w-full h-full opacity-0"
                            onChange={(e) => {
                              handleFileChange(e, idx);
                            }}
                          />
                          {fileArray?.[idx]?.data?.length ? (
                            <img
                              onClick={() => {
                                setFileArray(
                                  fileArray?.map((el, flIndex) =>
                                    idx === flIndex
                                      ? { uri: "", file: "", data: "" }
                                      : el
                                  )
                                );
                              }}
                              src={Images.deleteIcon}
                              className="absolute w-6 h-6 rounded-full shadow-xl -top-2 -right-2"
                              alt="delete"
                            />
                          ) : null}
                        </div>
                      );
                    })}
                </div>
              ) : (
                <div className="flex items-center justify-center w-full p-4 border-2 border-dashed border-blue rounded-lg relative bg-blue/10">
                  <div className="text-center">
                    <img
                      src={Images.attachmentIcon}
                      alt="attach"
                      className="w-8 h-8 mx-auto mb-2"
                    />
                    <p className="text-gray-400 font-medium">Click to add attachments</p>
                    <input
                      type="file"
                      accept="image/*,application/pdf"
                      className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                      onChange={(e) => {
                        handleFileChange(
                          e,
                          fileArray?.findIndex((el) => el?.uri?.length === 0)
                        );
                      }}
                    />
                  </div>
                </div>
              )}
            </div>

            {/* Submit Button */}
            <div className="flex justify-end space-x-4">
              <Button
                label="Cancel"
                className="px-6 py-3 text-xl font-bold !bg-gray-300 !text-black"
                onClick={onClose}
                disabled={creatingTicket}
              />
              <Button
                label={creatingTicket ? "Submitting..." : "Submit Ticket"}
                buttonColor="orange"
                className="px-6 py-3 text-xl font-bold"
                onClick={handleSubmit}
                disabled={!isButtonEnabled || isEmojiIncluded}
                loading={creatingTicket}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default CreateTicketModal;